import { GetPolicyDraftResponseType } from '@clearcover/core-app-types';
import { DriverOptionsResponse } from 'Policies/components/PolicyDriversSection/DriverForm';
import { GrantsType } from 'Shared/utils/auth';
import React, { useContext } from 'react';

type PolicyDraftDetails =
  | {
      isPolicyDraft: false;
      isRenewalDraft: false;
      draftType: undefined;
    }
  | {
      isPolicyDraft: true;
      isRenewalDraft: boolean;
      draftType: GetPolicyDraftResponseType['policyDraft']['draftType'];
    };
export type PolicyContextType = {
  details: {
    region: string;
    effectiveDate: string;
    timeZone: string | undefined;
    filingIdentifier: string;
    configDiscoveryParams: string;
    termId: string;
    mailingState: string;
    showDriverCfrs: boolean;
    askForUserReportedLicenseStatus: boolean;
    requiredAccessStatements?: boolean;
    showMilitarySection: boolean;
    showStudentSection: boolean;
    showEducation: boolean;
    showOccupation: boolean;
    showRegisteredToApplicantOrSpouse: boolean;
    showExcludedDriverGender: boolean;
    showExcludedDriverMaritalStatus: boolean;
    showPricingData: boolean;
  } & PolicyDraftDetails;
  person: {
    name: string;
  };
  flags: {
    renewalQuotesEnabled: boolean;
    mandatedDownPay: boolean;
    isFailingUnderwriting: boolean;
  };
  canEndorseDuringRegionalEvents: boolean;
  isEndorseablePolicy: boolean;
  systemOfRecord: 'one_inc_3' | 'one_inc_4' | 'clearcover' | 'missing';
  resourceEffectiveGrants: GrantsType;
  refetchHandler: () => void;
  loading: boolean;
  productSegment: string;
  draftList: Array<{
    id: string;
    type:
      | 'mid_term'
      | 'renewal'
      | 'renewal_quote'
      | 'cancellation'
      | 'reinstatement';
    effectivePeriod: {
      startAt: string;
      endAt: string;
    };
  }>;
  removeDraft: (draftId: string) => void;
  setDriverOptions: (driverOptions: DriverOptionsResponse | null) => void;
  driverOptions: DriverOptionsResponse | null;
};

export const PolicyContext = React.createContext<PolicyContextType>({
  details: {
    region: '',
    effectiveDate: '',
    timeZone: undefined,
    filingIdentifier: '',
    configDiscoveryParams: '',
    isPolicyDraft: false,
    draftType: undefined,
    isRenewalDraft: false,
    termId: '',
    mailingState: '',
    showDriverCfrs: false,
    askForUserReportedLicenseStatus: false,
    requiredAccessStatements: false,
    showMilitarySection: true,
    showStudentSection: true,
    showEducation: true,
    showOccupation: true,
    showRegisteredToApplicantOrSpouse: true,
    showExcludedDriverGender: false,
    showExcludedDriverMaritalStatus: false,
    showPricingData: true,
  },
  person: {
    name: '',
  },
  flags: {
    renewalQuotesEnabled: false,
    mandatedDownPay: false,
    isFailingUnderwriting: false,
  },
  canEndorseDuringRegionalEvents: true,
  isEndorseablePolicy: true,
  systemOfRecord: 'clearcover',
  resourceEffectiveGrants: [],
  refetchHandler: () => {},
  loading: false,
  productSegment: 'standardAuto',
  draftList: [],
  removeDraft: () => {},
  setDriverOptions: () => {},
  driverOptions: null,
});

export const usePolicyContext = () => useContext(PolicyContext);
